/**
 * DEMO ONLY — mock data for the dynamic pricing slabs demo.
 * Numbers are loosely based on the real 2026 price list but are not authoritative.
 */

export const DEFAULT_GLOBAL_BANDS = [
  { minQty: 5, maxQty: 14, label: "5-14 (first order)" },
  { minQty: 15, maxQty: 29, label: "15-29" },
  { minQty: 30, maxQty: 99, label: "30-99" },
  { minQty: 100, maxQty: 149, label: "100-149" },
  { minQty: 150, maxQty: null, label: "150+" },
];

/** The split the 2026 import introduces: 30-99 -> 30-49 + 50-99. */
export const SPLIT_GLOBAL_BANDS = [
  { minQty: 5, maxQty: 14, label: "5-14 (first order)" },
  { minQty: 15, maxQty: 29, label: "15-29" },
  { minQty: 30, maxQty: 49, label: "30-49" },
  { minQty: 50, maxQty: 99, label: "50-99" },
  { minQty: 100, maxQty: 149, label: "100-149" },
  { minQty: 150, maxQty: null, label: "150+" },
];

/** Exception band sets, exactly as the Excel shows them (07 §17 Q3). */
export const BAND_PRESETS = {
  "Ice Hockey": [
    { minQty: 5, maxQty: 14, label: "5-14 (first order)" },
    { minQty: 15, maxQty: 29, label: "15-29" },
    { minQty: 30, maxQty: 49, label: "30-49" },
    { minQty: 100, maxQty: 149, label: "100-149" },
    { minQty: 150, maxQty: null, label: "150+" },
  ],
  "Yarn-Dyed (min. 50)": [
    { minQty: 50, maxQty: 149, label: "50-149" },
    { minQty: 150, maxQty: null, label: "150+" },
  ],
  "Premium Varsity Jacket": [
    { minQty: 1, maxQty: 14, label: "1-14 (first order)" },
    { minQty: 50, maxQty: 99, label: "50-99" },
    { minQty: 100, maxQty: 149, label: "100-149" },
    { minQty: 150, maxQty: null, label: "150+" },
  ],
};

export const SPORTS = [
  { id: 1, name: "GENERIC" },
  { id: 2, name: "ICE HOCKEY" },
  { id: 3, name: "YARN-DYED" },
];

export const ITEMS = [
  { id: 1, name: "CS Polo Shirt", sportId: 1 },
  { id: 2, name: "CS Tee", sportId: 1 },
  { id: 3, name: "Ice Hockey Jersey", sportId: 2 },
  { id: 4, name: "Premium Varsity Jacket", sportId: 1 },
  { id: 5, name: "Horizontal Rugby Jumper", sportId: 3 },
];

export const FABRICS = [
  { id: 1, name: "150gsm Vantage" },
  { id: 2, name: "Vantage Silver" },
  { id: 3, name: "165gsm Polyester Coolmesh/Ultradry" },
  { id: 4, name: "125gsm Velocity" },
  { id: 5, name: "150gsm Ultradry II" },
  { id: 6, name: "Pre-Shrunk 360gsm Poly Cotton" },
];

export const LININGS = [
  { id: 1, name: "1 lining: mesh" },
  { id: 2, name: "1 lining: cotton" },
  { id: 3, name: "1 lining: fleece" },
];

/**
 * Sample price rows. `valuesCents` is keyed by band label.
 * Rows with `presetName` use that preset's bands instead of the global set.
 */
export const SAMPLE_PRICES = [
  {
    id: 1,
    sportId: 1,
    itemId: 1,
    fabricId: 1,
    liningIds: [],
    presetName: null,
    fobCents: 1059,
    status: "active",
    valuesCents: {
      "5-14 (first order)": 2850,
      "15-29": 3500,
      "30-99": 3150,
      "100-149": 3000,
      "150+": 2850,
    },
  },
  {
    id: 2,
    sportId: 1,
    itemId: 1,
    fabricId: 2,
    liningIds: [],
    presetName: null,
    fobCents: 1040,
    status: "active",
    valuesCents: {
      "5-14 (first order)": 3150,
      "15-29": 3850,
      "30-99": 3450,
      "100-149": 3300,
      "150+": 3150,
    },
  },
  {
    id: 3,
    sportId: 1,
    itemId: 2,
    fabricId: 4,
    liningIds: [],
    presetName: null,
    fobCents: 978,
    status: "active",
    valuesCents: {
      "5-14 (first order)": 2550,
      "15-29": 3100,
      "30-99": 2800,
      "100-149": 2700,
      "150+": 2550,
    },
  },
  {
    id: 4,
    sportId: 1,
    itemId: 2,
    fabricId: 3,
    liningIds: [],
    presetName: null,
    fobCents: 849,
    status: "inactive",
    valuesCents: {
      "5-14 (first order)": 2600,
      "15-29": 3150,
      "30-99": 2850,
      "100-149": 2750,
      "150+": 2600,
    },
  },
  {
    id: 5,
    sportId: 2,
    itemId: 3,
    fabricId: 5,
    liningIds: [],
    presetName: "Ice Hockey",
    fobCents: 1793,
    status: "active",
    valuesCents: {
      "5-14 (first order)": 4500,
      "15-29": 5500,
      "30-49": 5200,
      "100-149": 4750,
      "150+": 4500,
    },
  },
  {
    id: 6,
    sportId: 1,
    itemId: 4,
    fabricId: 6,
    liningIds: [],
    presetName: "Premium Varsity Jacket",
    fobCents: 4100,
    status: "active",
    valuesCents: {
      "1-14 (first order)": 12508,
      "50-99": 12508,
      "100-149": 11448,
      "150+": 10600,
    },
  },
  {
    id: 7,
    sportId: 3,
    itemId: 5,
    fabricId: 6,
    liningIds: [],
    presetName: "Yarn-Dyed (min. 50)",
    fobCents: 5600,
    status: "active",
    valuesCents: {
      "50-149": 7800,
      "150+": 7500,
    },
  },
];

export const INITIAL_STATE = {
  globalBands: DEFAULT_GLOBAL_BANDS,
  prices: SAMPLE_PRICES,
  showInactive: false,
};
